import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  inject,
  Output,
  EventEmitter,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationService, MessageService, MenuItem } from 'primeng/api';
import { Button } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import {
  EnumService,
  DocAnexoService,
  BolseiroService,
} from '@app/features-modules/gestao-bolseiros/components/api/services';
import { UploadDocAnexoModalComponent } from '../upload-doc-anexo-modal/upload-doc-anexo-modal.component';
import { Pagination } from '@shared/model/pagination.model';
import { DrawerModule } from 'primeng/drawer';
import { DataVoidComponent } from '@app/shared/components/data-void/data-void.component';
import { TooltipModule } from 'primeng/tooltip';
import {
  TableComponent,
  TableColumn,
} from '@shared/components/table/table.component';
import { ApiService } from '@core/providers/http';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { AvatarModule } from 'primeng/avatar';
import { getInitials } from '@shared/pipes/initials.pipe';

@Component({
  selector: 'app-doc-anexo-drawer',
  standalone: true,
  imports: [
    CommonModule,
    Button,
    TagModule,
    TableModule,
    DialogModule,
    SelectModule,
    ReactiveFormsModule,
    InputText,
    DrawerModule,
    DataVoidComponent,
    Button,
    TooltipModule,
    TableComponent,
    ToastModule,
    ConfirmDialogModule,
    AvatarModule,
  ],
  templateUrl: './doc-anexo-drawer.component.html',
  styleUrls: ['./doc-anexo-drawer.component.scss'],
  providers: [
    DialogService,
    BolseiroService,
    ConfirmationService,
    MessageService,
    ApiService,
  ],
})
export class DocAnexoDrawerComponent implements OnInit {
  imageError = false;

  getInitials(name?: string | null): string {
    return getInitials(name);
  }
  @Input({ required: true }) entidadeId!: number; // SEMPRE: id da BOLSA
  @Input() estudanteNome?: string;
  @Input() estudanteSelected: any;
  @Input() numeroBI?: string;
  @Output() onCloseModal = new EventEmitter<boolean>();
  imagePreview: string | ArrayBuffer | null = null; // To store image as Base64 URL

  bolseiroID!: string;
  listaCompetencias: any[] = [];

  showDetalhe: boolean = false;
  isLoading: boolean = false;
  perfilEstudante: any;
  bolseiroDetalhe: any = null;
  private docSrv = inject(DocAnexoService);
  private enums = inject(EnumService);
  private bolseiroService = inject(BolseiroService);
  private dialog = inject(DialogService);
  private confirm = inject(ConfirmationService);
  private msg = inject(MessageService);
  private fb = inject(FormBuilder);
  columns: TableColumn[] = [
    {
      header: 'Tipo',
      field: 'tipoDocumentoDescricao',
      type: 'text',
      tooltip: 'Tipo de Documento',
    },
    // 👇 NOVA COLUNA: ATIVO
    {
      header: 'Activo',
      type: 'tag',
      valueGetter: (r) => (r?.activo ? 'Activo' : 'Inactivo'),
      tagSeverityFunction: (r) => (r?.activo ? 'success' : 'danger'),
      tooltip: 'Estado de Activação',
    },
    // --- COLUNA EXISTENTE: ESTADO ---
    {
      header: 'Estado',
      type: 'tag',
      valueGetter: (r) => r?.estadoDocumentoDescricao ?? '—',
      tagSeverityFunction: (r) => this.getDocSeverity(r),
      tooltip: 'Estado do Documento',
    },
    {
      header: 'Emissão',
      type: 'text',
      valueGetter: (r) => r?.dataEmissaoFormatada || r?.dataEmissao,
      formatter: (v) => this.formatDate(v),
    },
    {
      header: 'Actualização',
      type: 'text',
      valueGetter: (r) => r?.dataModificacaoFormatada || r?.dataModificacao,
      formatter: (v) => this.formatDate(v),
    },
  ];

  showFilter = false;
  form!: FormGroup;

  // enums
  tipoDocOptions: { id: number; designacao: string }[] = [];
  estadoDocOptions: { id: number; designacao: string }[] = [];

  // dados
  pagination = new Pagination<any>();
  rows: any[] = [];
  loading = false;

  ativoOptions = [
    { id: true, designacao: 'Activo' },
    { id: false, designacao: 'Inactivo' },
  ];

  // por padrão: FICHA DE CADASTRO
  readonly TIPO_FICHA_CADASTRO = 5;
  readonly ALLOWED_DOCS = [
    'MEMORANDO DE PAGAMENTO DO SUBSIDIO DE APOIO Á FORMAÇÃO',
    'CANCELAMENTO DE BOLSA',
    'MEMORANDO DE APOIO PARA A DEFESA DA MONOGRAFIA',
    'DECLARAÇÃO PARA VISTOS - EMBAIXADAS',
    'FICHA DE CADASTRO',
  ] as const;

  ngOnInit(): void {
    this.form = this.fb.group({
      TipoDocumento: [''],
      EstadoDocumento: [null],
      UtilizadorID: [null],
      Ativo: [null],
      PageNumber: [1],
      PageSize: [10],
    });

    this.loadEnums();
    this.fetch();

    const id = Number(this.estudanteSelected?.id);
    if (id) this.getPhotoEstudante(id);
  }

  loadEnums() {
    this.enums.getAllTipoDocumentoEnum().subscribe((r) => {
      if (r?.data) {
        this.tipoDocOptions = Object.entries(r.data)
          .map(([id, nome]) => ({ id: +id, designacao: String(nome) }))
          .filter((opt) => this.ALLOWED_DOCS.includes(opt.designacao as any))
          .sort(
            (a, b) =>
              this.ALLOWED_DOCS.indexOf(a.designacao as any) -
              this.ALLOWED_DOCS.indexOf(b.designacao as any)
          );
      }
    });
    this.enums.getAllEnumEstadoDocumento().subscribe((r) => {
      if (r?.data) {
        this.estadoDocOptions = Object.entries(r.data).map(
          ([id, nome]: any) => ({
            id: +id,
            designacao: nome,
          })
        );
      }
    });
  }

  closeModal() {
    this.onCloseModal.emit(true);
  }

  fetch(extra?: Record<string, any>) {
    this.loading = true;

    const raw = {
      ...this.form.getRawValue(),
      EntidadeID: this.entidadeId,
      ...(extra ?? {}),
    };

    const params = Object.fromEntries(
      Object.entries(raw).filter(
        ([, v]) => v !== null && v !== undefined && v !== ''
      )
    );

    this.docSrv.list(params).subscribe({
      next: (res) => {
        const flat = this.flattenHistorico(res?.data ?? []);

        // monta a paginação para o <app-table>
        this.pagination = new Pagination<any>().deserialize(res);
        this.pagination.data = flat;
        this.pagination.totalRecords = res?.totalRecords ?? flat.length;
        this.pagination.records = flat.length;
        this.pagination.pageSize = this.form.value.PageSize;

        this.rows = flat; // útil se quiser alternar para p-table alguma hora
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.msg.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Falha ao carregar histórico de documentos.',
        });
        console.error(err);
      },
    });
  }

  // converte a estrutura { id, historicoDocumento[] } → linhas planas
  private flattenHistorico(items: any[]): any[] {
    const out: any[] = [];
    items?.forEach((x: any) => {
      (x.historicoDocumento ?? []).forEach((h: any) => {
        out.push({ ...h, entidadeID: x.entidadeID });
      });
    });
    return out.sort(
      (a, b) =>
        new Date(b.dataEmissao).getTime() - new Date(a.dataEmissao).getTime()
    );
  }

  onPageChange({ page, rows }: any) {
    this.form.patchValue({ PageNumber: page + 1, PageSize: rows });
    this.fetch();
  }

  openUpload() {
    // confirmação de descontinuidade
    // this.confirm.confirm({
    //   header: 'Substituir documento?',
    //   icon: 'pi pi-exclamation-triangle',
    //   message: 'O documento anterior será descontinuado. Deseja continuar?',
    //   acceptLabel: 'Sim, continuar',
    //   rejectLabel: 'Cancelar',
    //   accept: () => this.openUploadModal(),
    // });

    this.openUploadModal();
  }

  openUploadModal() {
    const ref = this.dialog.open(UploadDocAnexoModalComponent, {
      data: {
        entidadeID: this.entidadeId,
        tipoDocumentoPadrao:
          this.form.value['TipoDocumento'] ?? this.TIPO_FICHA_CADASTRO,
      },
      showHeader: false,
      header: 'Carregar documento',
      width: '40rem',
      modal: true,
    });

    // Padrão pedido (mergeMap/EMPTY) está dentro do componente de upload;
    // aqui apenas reagimos ao fechamento para recarregar.
    ref.onClose.subscribe((ok: boolean) => {
      if (ok) {
        this.msg.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Documento carregado.',
        });
        this.fetch();
      } else {
        if (ok === false) {
          this.msg.add({
            severity: 'warn',
            summary: 'Não carregado',
            detail: 'O documento não foi carregado.',
          });
        }
        // se vier undefined, o usuário apenas fechou sem ação
      }
    });
  }

  /** Ações do menu por linha (o TableComponent injeta o rowData) */
  actions: MenuItem[] = [
    {
      label: 'Baixar',
      icon: 'pi pi-download',
      command: ($e: any) => this.download($e.item?.data),
    },
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      command: ($e: any) => {
        const row = $e.item?.data;
        this.confirm.confirm({
          header: 'Confirmar eliminação',
          icon: 'pi pi-exclamation-triangle',
          message: `Tem a certeza que deseja eliminar este documento? Esta ação não pode ser desfeita.`,
          acceptLabel: 'Sim, eliminar',
          rejectLabel: 'Cancelar',
          accept: () => {
            if (row?.id) {
              this.docSrv.delete(row.id).subscribe({
                next: () => {
                  this.msg.add({
                    severity: 'success',
                    summary: 'Sucesso',
                    detail: 'Documento eliminado.',
                  });
                  this.fetch();
                },
                error: (err) => {
                  this.msg.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Não foi possível eliminar o documento.',
                  });
                  console.error(err);
                },
              });
            }
          },
        });
      },
    },
  ];

  /** Handler do menu de ações do <app-table> */
  onActionSelected(event: any) {
    const { event: actionEvent, rowData } = event;
    this.actions
      .find((action) => action.label === actionEvent.item.label)
      ?.command?.({ item: rowData });
  }

  /** Severidade do estado para o <p-tag> interno do app-table */
  private getDocSeverity(
    row: any
  ):
    | 'success'
    | 'info'
    | 'warn'
    | 'danger'
    | 'secondary'
    | 'contrast'
    | 'primary' {
    const s = (row?.estadoDocumentoDescricao || '').toLowerCase();
    if (/(aprov|válid|val|emit|conclu|ativ)/.test(s)) return 'success';
    if (/(pend|anális|anal|process)/.test(s)) return 'info';
    if (/(rejeit|inválid|invalid|cancel|expir)/.test(s)) return 'danger';
    return 'secondary';
  }

  private formatDate(v: any): string {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('pt-PT');
  }

  download(row: any) {
    // usa os dados da linha; se faltar, cai para os defaults do filtro atual
    const entidadeID = row?.entidadeID ?? this.entidadeId;
    const tipoDocumento =
      row?.tipoDocumento ?? this.form.value['TipoDocumento'];

    console.log('Baixando doc', { entidadeID, tipoDocumento, row });

    this.docSrv.getFicheiroById(row.id).subscribe({
      next: (blob) => {
        const a = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        a.href = url;
        const nome = `${(row?.tipoDocumentoDescricao ?? 'documento')
          .toLowerCase()
          .replace(/\s+/g, '-')}-${entidadeID}.pdf`;
        a.download = nome;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        this.msg.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível baixar o documento.',
        });
        console.error(err);
      },
    });
  }

  applyFilters() {
    this.fetch();
    this.showFilter = false;
  }

  clearFilters() {
    this.form.reset({
      TipoDocumento: '',
      EstadoDocumento: null,
      UtilizadorID: null,
      PageNumber: 1,
      PageSize: 10,
    });
    this.fetch();
  }

  getPhotoEstudante(id: number) {
    this.bolseiroService.getBolseiroFotoById(id).subscribe({
      next: (response) => {
        if (!response || !response.type.startsWith('image/')) {
          console.warn('Resposta não é imagem. A usar placeholder.');
          this.imagePreview = null;
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          this.imagePreview = reader.result;
        };
        reader.readAsDataURL(response);
      },
      error: (err) => {
        console.error('Erro ao carregar imagem da instituição:', err);
        this.imagePreview = null;
      },
    });
  }
}
